import React, { useState } from 'react';
import { Card, CardContent, Typography, Stack, Alert, Button } from '@mui/material';
import Carousel from '../../common/carousel/index';
import PropTypes from 'prop-types';
import CardHeader from './card-header';
import CountDown from '../../common/countdown';
import { makePriceLabelRoundAE } from '../../../utils/helpers/make-price-label';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { isTimePassed } from '../../../utils/helpers/secondsToHMS';
import { ROUTE_PATH } from '../../../constants/route-constants';
import { AUCTION_TYPE, NUMBER, TOAST_OPTIONS } from '../../../constants/app-constants';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import BasicModal from '../../common/modal';
import BidModal from '../bid/index';
import { isValidArray } from '../../../utils/is-array';
import { prepareGADataAndSendEvent } from '../../../tracking';
import PageLoader from '../../common/page-loader';
import BoltIcon from '@mui/icons-material/Bolt';

const AuctionCard = ({ user, auction, carouselSettings, handleCardClick = () => { }, isSlider = true, isWatchList, isTimer = true, clsName = '', isSoldOut,
    handleImageClick, fetchFreshAuctions = () => { }, toggleWatchListConnect = () => { }, setupToastConnect }) => {
    const { appointmentId, lastHighestBid, anchorAmount, auctionEndTimestamp, images, code, highestBidForCurrentDealer, currentDealerBid, currentDealerHasBidded, pai, auctionType, watchlisted, ocbV2, thresholdBid } = auction || {};
    const isMyCarsSection = highestBidForCurrentDealer;
    const isMyBids = window.location.pathname === ROUTE_PATH.MY_CARS;
    const isOcb = auctionType === AUCTION_TYPE.OCB || auctionType === AUCTION_TYPE.PAI;
    const isUpcoming = auctionType === AUCTION_TYPE.UPCOMING;
    const [isBidOpen, setIsBidOpen] = useState(false);
    const [isFav, setIsFav] = useState(watchlisted);
    const [loading, setLoading] = useState(false);

    const handleClick = () => {
        if (isUpcoming) return;
        handleCardClick();
    };

    const handleRaiseBid = (event) => {
        event.preventDefault();
        event.stopPropagation();
        prepareGADataAndSendEvent('raiseBid', user, auction);
        setIsBidOpen(true);
    };
    const handleFavClick = (event) => {
        event.stopPropagation();
        event.preventDefault();
        setLoading(true);
        prepareGADataAndSendEvent('addToWatchList', user, auction, isSlider ? 'Listing page' : 'Detail Page', { eventLabel: !isFav });
        toggleWatchListConnect({ vehicleId: appointmentId, shortListed: !isFav }).then(() => {
            setupToastConnect({ message: `Your watch list updated!`, type: TOAST_OPTIONS.SUCCESS });
            setIsFav(!isFav);
        }).catch(() => {
            setupToastConnect({ message: `Your watch list update failed`, type: TOAST_OPTIONS.ERROR });
        }).finally(() => {
            setLoading(false);
        });
    };
    const handleCallBack = () => {
        setTimeout(() => {
            fetchFreshAuctions();
        }, NUMBER.TWO_THOUSAND);
    };

    const raiseBidFromListing = isSlider && currentDealerHasBidded && !highestBidForCurrentDealer && auctionType === AUCTION_TYPE.LIVE;
    const imagesToShow = (isValidArray(images) && (isUpcoming ? images?.slice(0, 1) : images)) || [];
    return (
        <React.Fragment>
            {loading && <PageLoader />}
            <Card sx={{ maxWidth: 460 }} className={`relative ${isUpcoming && 'bg-v-light-br opacity-70 '} ${clsName} ${isSlider ? 'text-light-border shadow border rounded-xl' : 'shadow-none p-0'} ${!isTimer && 'rounded-none'}`} onClick={handleClick}>
                {isSlider && <Carousel settings={isUpcoming ? carouselSettings || { slidesPerView: 1 } : {}} items={imagesToShow} code={code} pai={pai} handleImageClick={handleImageClick} />}
                <CardContent sx={{ marginBottom: 0, paddingBottom: `${isUpcoming ? '1em!important' : '0!important'}` }} className='bg-background relative'>
                    <CardHeader isWatchList={isWatchList} auction={auction} isFav={isFav} handleFavClick={handleFavClick} />
                    {((highestBidForCurrentDealer || !isSlider) && (currentDealerBid || isMyBids)) &&
                        <Alert severity={highestBidForCurrentDealer ? 'success' : 'warning'} classes={{ message: highestBidForCurrentDealer ? 'text-success' : 'text-secondary' }} icon={highestBidForCurrentDealer ? <ThumbUpAltIcon /> : <WarningAmberIcon />} className='font-bold my-4 py-0 bg-white'>
                            {highestBidForCurrentDealer ? (isSoldOut ? 'Congratulations! You won this auction.' : 'Your bid is highest!') : 'You have been outbid!'}
                        </Alert>
                    }
                    {raiseBidFromListing && <div onClick={handleRaiseBid}>Click here to <span className='pt-2.5 ml-1 text-lg text-secondary font-semibold inline-flex items-center justify-center'>Raise Bid <ArrowUpwardIcon /></span></div>}
                    {!isUpcoming && <Stack direction={'row'} justifyContent="space-between" gap={1} alignItems="center" className={`mt-3 ${isTimer ? '-ml-6' : '-mx-5'}`}>
                        <HighestBid isOcb={isOcb && !ocbV2} thresholdBid={thresholdBid} hasDealerBid={currentDealerHasBidded || currentDealerBid} bidValue={lastHighestBid || anchorAmount} isMyCarsSection={isMyCarsSection} isTimer={isTimer} />
                        {isTimer && <BidAcceptingTimer bidTimer={auctionEndTimestamp} isMyCarsSection={isMyCarsSection} />}
                    </Stack>}
                    {ocbV2 && isOcb && <div className='-mx-6 flex justify-center bg-tab-bg'>
                        <Button startIcon={<BoltIcon />} className='font-semibold capitalize'>One click buy &nbsp;{makePriceLabelRoundAE(thresholdBid)}</Button>
                    </div>}
                </CardContent>
            </Card>
            <BasicModal open={isBidOpen} handleClose={setIsBidOpen.bind(null, false)}>
                <BidModal bid={auction} handleCallBack={handleCallBack} isListing={true} endTime={auctionEndTimestamp} handleClose={setIsBidOpen.bind(null, false)} />
            </BasicModal>
        </React.Fragment>
    );
};

AuctionCard.propTypes = {
    isSlider: PropTypes.bool,
    isTimer: PropTypes.bool,
    isWatchList: PropTypes.bool,
    clsName: PropTypes.string,
    isSoldOut: PropTypes.bool,
    auction: PropTypes.object,
    user: PropTypes.object,
    handleImageClick: PropTypes.func,
    handleCardClick: PropTypes.func,
    carouselSettings: PropTypes.object,
    fetchFreshAuctions: PropTypes.func,
    toggleWatchListConnect: PropTypes.func,
    setupToastConnect: PropTypes.func
};

export default AuctionCard;

export const HighestBid = ({ bidValue, isMyCarsSection, isTimer, isOcb, hasDealerBid, thresholdBid }) => {
    return (
        <Stack direction={'row'} spacing={1.5} alignItems="center" justifyContent={'space-around'} className={`p-3 py-4 h-5 font-bold ${isMyCarsSection ? 'bg-success' : (hasDealerBid ? 'bg-danger' : 'bg-primary')} ${isTimer ? ' w-7/12 rounded-tr-2xl ' : ' w-full flex justify-center'}`}>
            <span className={`text-white font-bold text-base`}>{!isOcb ? 'Highest Bid' : 'One Click Buy'}</span>
            <span className={`text-lg  text-white ${isTimer && 'font-bold'}`}>{makePriceLabelRoundAE(isOcb ? (thresholdBid || bidValue) : bidValue)}</span>
        </Stack>
    );
};
HighestBid.propTypes = {
    bidValue: PropTypes.number,
    isMyCarsSection: PropTypes.bool,
    isTimer: PropTypes.bool,
    isMyBids: PropTypes.bool,
    isOcb: PropTypes.bool,
    hasDealerBid: PropTypes.bool,
    thresholdBid: PropTypes.string
};

export const BidAcceptingTimer = ({ bidTimer, isMyCarsSection }) => {
    return (
        <Stack direction={'column'} justifyContent="end" alignItems="end" marginTop={-1}>
            <Typography variant="body1" className={`text-primary font-bold ${isMyCarsSection && 'text-success'}`}>
                {isTimePassed(bidTimer) ? 'Auction Over' : isMyCarsSection ? 'Accepting Bids' : 'Time Remaining'}
            </Typography>
            <Typography variant="body1" component={'div'}>
                <CountDown isDate={true} date={new Date(bidTimer)} clsName='text-danger font-bold text-lg truncate' />
            </Typography>
        </Stack>
    );
};
BidAcceptingTimer.propTypes = {
    bidTimer: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    isMyCarsSection: PropTypes.bool
};